import React, {useEffect, useRef} from 'react';
import {Input} from 'antd';
import set from 'lodash/set';
import get from 'lodash/get';
import {AutosaveChanges, AutosaveFieldOptions, AutosaveFormOptions} from './rc-form/types';
import {AutosaveValues} from './state/types';
import {useSaveQueue} from './state/use-save-queue';
import {changeMode, controlValuesEqual, isTypingControl} from './rc-form/controls';

/**
 * Adds autosave to an existing Form.create form by replacing only its getFieldDecorator.
 * rc-form retains ownership of collection, normalization, validation, and control refs.
 * Network formatting and confirmations stay in the caller's onSave adapter.
 */
export function useAutosaveForm<Values extends object = AutosaveValues>(
    options: AutosaveFormOptions<Values>
) {
    const latest = useRef(options);
    latest.current = options;
    const controls = useRef(new Map<string, React.ReactElement>());
    const collectionTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const composing = useRef(new Set<string>());
    const flushAfterCollection = useRef(new Set<string>());
    const typingEvent = useRef(false);
    const initialValues: AutosaveValues = {};

    const queue = useSaveQueue({
        recordKey: options.recordKey,
        initialValues,
        isCollecting: (key) => collectionTimers.current.has(key) || composing.current.has(key),
        disabled: Boolean(options.disabled),
        delay: options.delay,
        onBusyChange: options.onBusyChange,
        isEqual: (key, left, right) =>
            options.isEqual
                ? options.isEqual(key, left, right)
                : controlValuesEqual(controls.current.get(key)!, left, right),
        reset: (keys) => options.form.resetFields(keys),
        validate: (keys) =>
            new Promise((resolve) => {
                options.form.validateFields(keys, (errors) => {
                    // Read from rc-form after normalization, including nested and custom event values.
                    resolve({
                        values: Object.fromEntries(
                            keys.map((key) => [key, options.form.getFieldValue(key)])
                        ),
                        invalidKeys: keys.filter((key) =>
                            Boolean(get(errors, key) || options.form.getFieldError(key)?.length)
                        ),
                    });
                });
            }),
        save: (values) => {
            const changes = {};
            Object.keys(values).forEach((key) => set(changes, key, values[key]));
            return options.onSave(changes as AutosaveChanges<Values>);
        },
    });

    /** Runs after rc-form's original change handler has stored the normalized field value. */
    function collect(key: string, mode: 'typing' | 'immediate') {
        clearTimeout(collectionTimers.current.get(key));
        const record = options.recordKey;
        collectionTimers.current.set(
            key,
            setTimeout(() => {
                collectionTimers.current.delete(key);
                if (record !== latest.current.recordKey || composing.current.has(key)) {
                    return;
                }

                queue.change(key, latest.current.form.getFieldValue(key), mode);
                if (flushAfterCollection.current.delete(key)) {
                    queue.flush(key);
                }
            }, 0)
        );
    }

    /** Blur/Enter may occur before the deferred collection callback; preserve that ordering. */
    function flushField(key: string) {
        if (collectionTimers.current.has(key)) {
            flushAfterCollection.current.add(key);
        } else if (composing.current.has(key)) {
            flushAfterCollection.current.add(key);
        } else {
            queue.flush(key);
        }
    }

    /** Decorates the original control, composing handlers instead of changing Form.create options. */
    function getFieldDecorator<T extends object = Record<string, unknown>>(
        name: keyof T,
        fieldOptions: AutosaveFieldOptions = {}
    ) {
        const key = String(name);
        const {autosave = 'auto', ...rcOptions} = fieldOptions;
        return (node: React.ReactNode): React.ReactNode => {
            if (!React.isValidElement(node) || autosave === false) {
                return options.form.getFieldDecorator<Record<string, unknown>>(
                    key,
                    rcOptions
                )(node);
            }

            const control = node as React.ReactElement;
            controls.current.set(key, control);
            initialValues[key] = Object.prototype.hasOwnProperty.call(rcOptions, 'initialValue')
                ? rcOptions.initialValue
                : options.form.getFieldValue(key);
            queue.register(key, initialValues[key]);
            const trigger = rcOptions.trigger || 'onChange';
            const typing =
                autosave === 'typing' || (autosave === 'auto' && isTypingControl(control));
            const handlers: Record<string, (...args: any[]) => void> = {};

            handlers[trigger] = (...args: any[]) => {
                control.props[trigger]?.(...args);
                const mode =
                    autosave === 'auto' && trigger === 'onBlur'
                        ? 'immediate'
                        : autosave === 'auto' && typingEvent.current
                        ? 'typing'
                        : changeMode(control, autosave, args[0]);
                collect(key, mode);
            };
            if (trigger !== 'onBlur') {
                handlers.onBlur = (...args: any[]) => {
                    control.props.onBlur?.(...args);
                    flushField(key);
                };
            }
            handlers.onKeyDown = (event: React.KeyboardEvent) => {
                control.props.onKeyDown?.(event);
                if (
                    event.key === 'Enter' &&
                    !event.defaultPrevented &&
                    !composing.current.has(key)
                ) {
                    // Enter remains a newline in textareas; explicit form submission stays caller-owned.
                    if (
                        control.type !== 'textarea' &&
                        control.type !== Input.TextArea &&
                        (event.target as HTMLElement).tagName !== 'TEXTAREA'
                    ) {
                        flushField(key);
                    }
                }
            };
            handlers.onCompositionStart = (...args: any[]) => {
                composing.current.add(key);
                queue.pause(key);
                control.props.onCompositionStart?.(...args);
            };
            handlers.onCompositionEnd = (...args: any[]) => {
                composing.current.delete(key);
                control.props.onCompositionEnd?.(...args);
                collect(key, typing ? 'typing' : 'immediate');
            };
            return options.form.getFieldDecorator<Record<string, unknown>>(
                key,
                rcOptions
            )(React.cloneElement(control, handlers));
        };
    }

    // Picker popup inputs live in portals and emit moment values, losing the DOM event.
    // Observe native input dispatch only to classify that same event; never read input text.
    useEffect(() => {
        let clearTyping: ReturnType<typeof setTimeout>;
        const onInput = (event: Event) => {
            const target = event.target as HTMLInputElement;
            typingEvent.current =
                ['INPUT', 'TEXTAREA'].includes(target.tagName) &&
                !['checkbox', 'radio', 'range', 'file'].includes(target.type);
            clearTimeout(clearTyping);
            clearTyping = setTimeout(() => {
                typingEvent.current = false;
            }, 0);
        };
        const onPointer = () => {
            typingEvent.current = false;
        };
        document.addEventListener('input', onInput, true);
        document.addEventListener('mousedown', onPointer, true);
        return () => {
            clearTimeout(clearTyping);
            document.removeEventListener('input', onInput, true);
            document.removeEventListener('mousedown', onPointer, true);
        };
    }, []);

    useEffect(() => {
        const timers = collectionTimers.current;
        const composingFields = composing.current;
        const flushes = flushAfterCollection.current;
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
            timers.clear();
            composingFields.clear();
            flushes.clear();
        };
    }, [options.recordKey]);

    /** Flushes events collected this tick before making pending edits eligible. */
    function flush(key?: string) {
        if (key) {
            flushField(key);
        } else {
            collectionTimers.current.forEach((_, field) => flushAfterCollection.current.add(field));
            queue.flush();
        }
    }

    return {getFieldDecorator, status: queue.status, retry: () => flush(), flush};
}
